import { useState, useEffect, useMemo, useCallback, } from 'react';

import { Line, Bar } from 'react-chartjs-2';
import {  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip as ChartTooltip, 
        Legend, Filler } from 'chart.js';

import { Box, Grid, Paper, Typography, CircularProgress, IconButton, Stack, TextField, Tooltip, } from '@mui/material';
import { ChevronLeft, ChevronRight, TableChartOutlined,SsidChart, BarChart, FiberManualRecord, Refresh, } from '@mui/icons-material';
import { LocalizationProvider, DatePicker,  } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import * as XLSX from "xlsx";
import moment from 'moment';

import { generateExcelFileName } from '../include'

ChartJS.register( CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, ChartTooltip, Legend, Filler );

const whiteBackgroundPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

export const AlertGR = (props) => {
  // 1. Criar a referência
  // const isFirstRender = useRef(true);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [minDate, setMinDate] = useState(moment("2022-08-01"));
  const [chartType, setChartType] = useState('line'); 

  const loadAlertHistory = useCallback(async () => {
    if (!props.api) return;
    
    setIsLoading(true);
    setHasError(false);
    const isCurrentMonth = selectedDate.isSame(moment(), 'month');
    const endDateValue = isCurrentMonth ? moment().endOf('day') : selectedDate.clone().endOf('month');
    const params = {
      startDate: selectedDate.clone().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endDate: endDateValue.format('YYYY-MM-DD HH:mm:ss'),
    };
    try {
      const res = await props.api.post('/alertgr/getHistory', params);
      const dataToSet = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      setHistoryData(dataToSet);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.api, selectedDate]);

  const fetchMinDate = useCallback(async () => {
    if (!props.api) return;

    try {
      const res = await props.api.get('/alertgr/getOldestDate');
      const data = res.data || res;
      if (data && data.min_date) setMinDate(moment(data.min_date));
    } catch (error) {
      console.error("Erro ao obter data limite:", error);
    }
  }, [props.api]);

  useEffect(() => { loadAlertHistory(); }, [loadAlertHistory]);

  useEffect(() => { fetchMinDate(); }, [fetchMinDate]);

  const handlePrevMonth = (e) => { e.stopPropagation(); setSelectedDate(prev => prev.clone().subtract(1, 'month')); };
  const handleNextMonth = (e) => { e.stopPropagation(); setSelectedDate(prev => prev.clone().add(1, 'month')); };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { 
        mode: 'index', 
        intersect: false,
        filter: (tooltipItem) => {
          const isCurrentMonth = selectedDate.isSame(moment(), 'month');
          const today = moment().date();
          const itemDay = tooltipItem.dataIndex + 1;
          return !(isCurrentMonth && itemDay > today);
        },
        callbacks: {
          label: (context) => {
            const isTrigger = context.dataset.label === 'TRIGGER';
            const dataIndex = context.dataIndex;
            const val = context.raw;
            if (!isTrigger) {
              return `${context.dataset.label}: ${val === null || val === undefined ? 'N/D' : val}`;
            }
            if (chartType === 'line') {
              const allDatasets = context.chart.data.datasets.filter(ds => ds.label !== 'TRIGGER');
              const nullDatasets = allDatasets.filter(ds => ds.data[dataIndex] === null);
              return nullDatasets.map(ds => `${ds.label}: N/D`);
            }
            return null;
          }
        }
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { 
        grid: { display: false },
        ticks: {
          color: (context) => {
            const isCurrentMonth = selectedDate.isSame(moment(), 'month');
            const today = moment().date();
            return (isCurrentMonth && (context.index + 1) > today) ? '#d1d1d1' : '#666'; 
          }
        }
      }
    }
  }), [selectedDate, chartType]);

  const datasets = useMemo(() => {
    if (!Array.isArray(historyData) || historyData.length === 0) return [];

    const sortedAlerts = historyData
      .filter((value, index, self) => 
        index === self.findIndex((t) => t.alert_name === value.alert_name)
      )
      .sort((a, b) => a.alert_type - b.alert_type);

    const dynamicAlertNames = sortedAlerts.map(a => a.alert_name);

    const availableColors = [
      { color: '#2196f3', bg: 'rgba(33, 150, 243, 0.1)' },
      { color: '#fffb00', bg: 'rgba(255, 234, 0, 0.22)' },
      { color: '#f44336', bg: 'rgba(244, 67, 54, 0.1)' }
    ];

    const colorPalette = {};
    sortedAlerts.forEach((alert, index) => {
      colorPalette[alert.alert_name] = availableColors[index] || { color: '#9e9e9e', bg: 'rgba(0,0,0,0.1)' };
    });
    
    const daysInMonth = selectedDate.daysInMonth();
    const isCurrentMonth = selectedDate.isSame(moment(), 'month');
    const today = moment().startOf('day');

    return dynamicAlertNames.map(name => {
      const labels = [];
      const dataValues = [];
      const style = colorPalette[name]; 

      for (let day = 1; day <= daysInMonth; day++) {
        const currentLoopDate = selectedDate.clone().date(day).startOf('day');
        labels.push(currentLoopDate.format('DD/MM'));

        if (isCurrentMonth && currentLoopDate.isAfter(today)) {
          dataValues.push(null);
        } else {
          const dateStr = currentLoopDate.format('YYYY-MM-DD');
          const found = historyData.find(d => 
            moment(d.created_at).format('YYYY-MM-DD') === dateStr && d.alert_name === name
          );
          dataValues.push(found ? found.alert_count : null);
        }
      }

      const dummyData = labels.map((_, i) => {
        const date = selectedDate.clone().date(i + 1).startOf('day');
        return (isCurrentMonth && date.isAfter(today)) ? null : 0;
      });

      return {
        title: name,
        borderColor: style.color,
        data: {
          labels,
          datasets: [
            {
              label: name,
              data: dataValues,
              borderColor: style.color,
              backgroundColor: chartType === 'line' ? style.bg : style.color,
              stack: 'combined',
              fill: chartType === 'line',
              pointRadius: dataValues.map(v => v === null ? 0 : 4),
              tension: 0.3,
              spanGaps: false
            },
            {
              label: 'TRIGGER',
              data: dummyData,
              stack: 'combined',  // sobreposto ao outro para nao dividir a largura
              borderColor: 'transparent',   // para nao apafrecer
              backgroundColor: 'transparent',
              pointRadius: 0,
              fill: false
            }
          ]
        }
      };
    });
  }, [historyData, selectedDate, chartType]);

  const exportToExcel = () => {
    if (!historyData || historyData.length === 0) return;

    setIsExporting(true);
    try {
      const isCurrentMonth = selectedDate.isSame(moment(), 'month');
      const lastDayToExport = isCurrentMonth ? moment().date() : selectedDate.daysInMonth();
      const dynamicAlertNames = historyData
        .filter((v, i, a) => i === a.findIndex(t => t.alert_name === v.alert_name))
        .sort((a, b) => a.alert_type - b.alert_type)
        .map(a => a.alert_name);
              
      const rows = [];
      for (let day = 1; day <= lastDayToExport; day++) {
        const currentDate = selectedDate.clone().date(day);
        const dateStr = currentDate.format('YYYY-MM-DD');
        const getCount = (name) => {
          const found = historyData.find(d => moment(d.created_at).format('YYYY-MM-DD') === dateStr && d.alert_name === name);
          return found ? found.alert_count : ""; 
        };
        rows.push([currentDate.format('DD/MM/YYYY'), ...dynamicAlertNames.map(name => getCount(name))]);
      }
      const sheetData = [["Histórico de Alertas"], [`Competência: ${selectedDate.format('MMMM/YYYY')}`], [], ["Data", ...dynamicAlertNames], ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet['!cols'] = [{ wch: 11 }, ...dynamicAlertNames.map(() => ({ wch: 15 }))];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Alertas");
      XLSX.writeFile(workbook, generateExcelFileName('Alertas'));
    } catch (error) {
      console.error("Erro Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)', width: '100%', overflow: 'hidden', backgroundColor: '#fff' }}>
        
        <div style={{ flex: '0 0 auto', padding: '15px 15px 10px 25px', backgroundColor: '#fff', zIndex: 10 }}>
          <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
            <DatePicker
              label="Competência (Mês/Ano)"
              views={['year', 'month']}
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              minDate={minDate}
              maxDate={moment()}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 300 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {/* Mês Anterior */}
                        <Tooltip title="Mês anterior" arrow>
                          <span>
                            <IconButton size="small" onClick={handlePrevMonth} disabled={selectedDate.isSameOrBefore(minDate, 'month')}>
                              <ChevronLeft fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Mês Atual */}
                        <Tooltip title="Mês corrente" arrow>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedDate(moment()); 
                              }} 
                              // Desabilita se já estiver no mês atual para evitar recargas desnecessárias
                              disabled={selectedDate.isSame(moment(), 'month')}
                            >
                              <FiberManualRecord fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Mês Seguinte */}
                        <Tooltip title="Mês seguinte" arrow>
                          <span>
                            <IconButton size="small" onClick={handleNextMonth} disabled={selectedDate.isSameOrAfter(moment(), 'month')}>
                              <ChevronRight fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Tooltip title="Atualizar" arrow>
              <span>
                <IconButton onClick={loadAlertHistory} disabled={isLoading} color="default">
                  <Refresh sx={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </span>
            </Tooltip>

            {/* criar afastamento */}
            <Box sx={{ width: 6 }} />

            <Tooltip title="Gráfico de Linha" arrow>
              <IconButton onClick={() => setChartType('line')} sx={{  border: '1px solid #e0e0e0', borderRadius: '4px',  color: chartType === 'line' ? '#1976d2' : '#000' }}>
                <SsidChart />
              </IconButton>
            </Tooltip>

            <Tooltip title="Gráfico de Barras" arrow>
              <IconButton onClick={() => setChartType('bar')} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', color: chartType === 'bar' ? '#1976d2' : '#000' }}>
                <BarChart />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exportar para Excel" arrow>
              <span>
                <IconButton sx={{ ml: 2, color: '#106436' }} onClick={exportToExcel} disabled={isLoading || isExporting || historyData.length === 0}>
                  {isExporting ? <CircularProgress size={24} color="inherit" /> : <TableChartOutlined />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </div>

        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '10px 25px 25px 25px' }}>
          {hasError ? (
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff5f5' }}>
              <Typography color="error">Erro ao carregar dados.</Typography>
            </Paper>
          ) : isLoading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            /* --- SE NAO TEM HISTORICO --- */
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, textAlign: 'center', backgroundColor: '#f8f9fa', 
                border: '1px dashed #ced4da', borderRadius: '8px', mt: 2 
              }}
            >
              <Typography variant="h6" color="textSecondary">
                Sem histórico de alertas no mês selecionado.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={4}>
              {datasets.map((chart, index) => (
                <Grid item xs={12} key={index}>
                  <Paper elevation={2} sx={{ p: 2, height: '450px', borderRadius: '8px', borderTop: `6px solid ${chart.borderColor}`, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1 }}>Documentos {chart.title}</Typography>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>
                      {chartType === 'line' ? (
                        <Line data={chart.data} options={chartOptions} plugins={[whiteBackgroundPlugin]} />
                      ) : (
                        <Bar data={chart.data} options={chartOptions} plugins={[whiteBackgroundPlugin]} />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    </LocalizationProvider>
  );
};

